import { useEffect, useCallback, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LogOut, MessageSquare, X, ChevronRight } from 'lucide-react';
import useInterviewStore from '../stores/interviewStore';
import { BACKEND_URL } from '../lib/api';
import { startPcmCapture } from '../lib/pcmStreamer';
import { toast } from '../components/ui/sonner';
import WebcamPanel from '../components/interview/WebcamPanel';
import SphereCanvas from '../components/interview/SphereCanvas';
import TranscriptPanel from '../components/interview/TranscriptPanel';
import InterviewControls from '../components/interview/InterviewControls';
import PreInterviewGate from '../components/interview/PreInterviewGate';

/** Builds the streaming-STT WebSocket URL, carrying the session token as a
 * query param since browsers can't set custom headers on a WS handshake. */
function buildStreamUrl(sessionId) {
  const token = sessionStorage.getItem('botview_session_token') || '';
  const wsBase = BACKEND_URL.replace(/^http/, 'ws');
  return `${wsBase}/api/interview/${sessionId}/stream-audio?token=${encodeURIComponent(token)}`;
}

/**
 * InterviewRoom — the core interview experience.
 * Always dark theme regardless of global toggle.
 */
export default function InterviewRoom() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [showTranscript, setShowTranscript] = useState(true);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [recording, setRecording] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  // Consent + camera gate: nothing about the interview (including the AI's
  // opening message) starts until this is true.
  const [gatePassed, setGatePassed] = useState(false);
  const ttsRef = useRef(null);
  const wsRef = useRef(null);
  const pcmRef = useRef(null);
  // Safety-net MediaRecorder: records the full answer as a webm blob in
  // parallel with the PCM stream, so a failed/dropped WebSocket can still be
  // transcribed via the REST /audio fallback instead of losing the answer.
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const mediaChunksRef = useRef([]);
  // Set the instant the student clicks stop (while streaming is still being
  // awaited) so the safety-net capture freezes at their actual answer instead
  // of continuing to record dead air through the wait + retry window.
  const safetyBlobPromiseRef = useRef(null);
  const streamingActiveRef = useRef(false);
  const finalizedRef = useRef(false);
  const stopTimeoutRef = useRef(null);
  // Tracks the pending `setTimeout(() => speakText(...), 400)` after each AI
  // response, and whether the interview has ended — window.speechSynthesis is
  // a global browser API, so a stale in-flight response arriving just as/after
  // the student ends the interview (or the component unmounts) would otherwise
  // still call .speak() and start talking on a completely different page.
  const speakTimeoutRef = useRef(null);
  const endedRef = useRef(false);

  const {
    session, loading, error,
    transcript, aiResponding, isInterviewOver, sphereState,
    cameraOn,
    loadSession, startInterview, submitAnswer, submitAudioAnswer, commitStreamedTurn, setAiResponding, endInterview,
    setSphereState, setCameraOn, reset,
  } = useInterviewStore();

  // Load session on mount
  useEffect(() => {
    reset();
    loadSession(sessionId);
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Start interview once session loads AND the consent/camera gate has passed
  const hasStarted = useRef(false);
  useEffect(() => {
    if (session && gatePassed && !hasStarted.current) {
      hasStarted.current = true;
      (async () => {
        const text = await startInterview(sessionId);
        if (text) speakText(text);
      })();
    }
  }, [session, sessionId, gatePassed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Chrome/most browsers load speechSynthesis voices asynchronously — the
  // very first getVoices() call (e.g. for the opening intro line) often
  // returns an empty list before the browser has finished loading them,
  // which silently fell back to the OS default (often a male voice) while
  // every later turn — called after voices had finished loading — correctly
  // picked the preferred voice. Memoized so every speakText() call awaits
  // the SAME resolution instead of racing its own independent listener.
  const voicesReadyRef = useRef(null);
  const waitForVoices = useCallback(() => {
    if (!voicesReadyRef.current) {
      voicesReadyRef.current = new Promise((resolve) => {
        const existing = window.speechSynthesis?.getVoices() || [];
        if (existing.length > 0) {
          resolve(existing);
          return;
        }
        const onVoicesChanged = () => {
          window.speechSynthesis?.removeEventListener('voiceschanged', onVoicesChanged);
          resolve(window.speechSynthesis?.getVoices() || []);
        };
        window.speechSynthesis?.addEventListener('voiceschanged', onVoicesChanged);
        // Safety net for browsers that never fire voiceschanged.
        setTimeout(() => {
          window.speechSynthesis?.removeEventListener('voiceschanged', onVoicesChanged);
          resolve(window.speechSynthesis?.getVoices() || []);
        }, 1000);
      });
    }
    return voicesReadyRef.current;
  }, []);

  // Start warming the voice list as soon as the room mounts, well before
  // the intro line is ready to speak (session load + gate consent both
  // take longer than voice loading normally does).
  useEffect(() => {
    waitForVoices();
  }, [waitForVoices]);

  /**
   * Select the best available TTS voice from an already-loaded voice list.
   * Prefer voices containing "Google", "Neural", "Natural", or "Aria" in their name.
   * Falls back to default if none found.
   * NOTE: Can be upgraded to a premium TTS API (ElevenLabs / Azure Neural TTS) later.
   */
  const getBestVoice = useCallback((voices) => {
    const preferred = ['Google', 'Neural', 'Natural', 'Aria'];
    // Try English voices matching preferred keywords
    for (const keyword of preferred) {
      const match = voices.find(
        (v) => v.name.includes(keyword) && v.lang.startsWith('en')
      );
      if (match) return match;
    }
    // Fallback: any English voice
    return voices.find((v) => v.lang.startsWith('en')) || null;
  }, []);

  // TTS — speak AI text and drive sphere state
  const speakText = useCallback(async (text) => {
    if (!text || typeof window === 'undefined' || endedRef.current) return;
    window.speechSynthesis?.cancel();
    const voices = await waitForVoices();
    if (endedRef.current) return; // interview may have ended while voices loaded
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    const voice = getBestVoice(voices);
    if (voice) utterance.voice = voice;
    utterance.onstart = () => setSphereState('speaking');
    utterance.onend = () => setSphereState('idle');
    utterance.onerror = () => setSphereState('idle');
    ttsRef.current = utterance;
    window.speechSynthesis?.speak(utterance);
  }, [setSphereState, getBestVoice, waitForVoices]);

  // Schedules the post-response speakText() call and tracks the timeout handle
  // so it can be cancelled if the interview ends before it fires.
  const scheduleSpeak = useCallback((text) => {
    if (speakTimeoutRef.current) clearTimeout(speakTimeoutRef.current);
    speakTimeoutRef.current = setTimeout(() => {
      speakTimeoutRef.current = null;
      speakText(text);
    }, 400);
  }, [speakText]);

  // Submit text answer (hard-guarded against double submit)
  const submittingRef = useRef(false);
  const handleSubmitText = useCallback(async (text) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      window.speechSynthesis?.cancel();
      setSphereState('idle');
      const aiText = await submitAnswer(sessionId, text);
      if (aiText) {
        setSphereState('reviewing');
        scheduleSpeak(aiText);
      }
    } finally {
      submittingRef.current = false;
    }
  }, [sessionId, submitAnswer, setSphereState, scheduleSpeak]);

  // Blob-based audio submit (webm → REST /audio). Used directly when the
  // streaming WS never became available, and as the fallback target when it
  // fails or drops mid-answer (see fallbackToBlobSubmit below).
  const handleSubmitAudio = useCallback(async (blob) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      window.speechSynthesis?.cancel();
      setSphereState('idle');
      const aiText = await submitAudioAnswer(sessionId, blob);
      if (aiText) {
        setSphereState('reviewing');
        scheduleSpeak(aiText);
      }
    } finally {
      submittingRef.current = false;
    }
  }, [sessionId, submitAudioAnswer, setSphereState, scheduleSpeak]);

  // Starts the safety-net MediaRecorder alongside the PCM stream.
  const startSafetyRecorder = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const recorder = new MediaRecorder(stream);
    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    recorder.start();
    mediaRecorderRef.current = recorder;
    mediaStreamRef.current = stream;
    mediaChunksRef.current = chunks;
  }, []);

  // Stops the safety-net recorder and resolves with the captured blob (or
  // null if nothing was captured).
  const stopSafetyRecorder = useCallback(() => {
    return new Promise((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') {
        resolve(null);
        return;
      }
      recorder.onstop = () => {
        const blob = mediaChunksRef.current.length
          ? new Blob(mediaChunksRef.current, { type: recorder.mimeType || 'audio/webm' })
          : null;
        mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
        mediaStreamRef.current = null;
        mediaRecorderRef.current = null;
        mediaChunksRef.current = [];
        resolve(blob);
      };
      recorder.stop();
    });
  }, []);

  // Discards the safety-net recorder without reading its data (streaming
  // succeeded, so the parallel blob recording is no longer needed).
  const discardSafetyRecorder = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null;
      recorder.stop();
    }
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
    mediaRecorderRef.current = null;
    mediaChunksRef.current = [];
  }, []);

  // Tears down the streaming WS/PCM path and submits whatever the
  // safety-net recorder captured via the REST fallback. `failureMessage`
  // is only passed (and toasted) for genuine failures — the deliberate
  // "streaming was never available" path calls this silently.
  const fallbackToBlobSubmit = useCallback(async (failureMessage) => {
    if (finalizedRef.current) return;
    finalizedRef.current = true;
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
    if (failureMessage) toast.error(failureMessage);

    wsRef.current?.close();
    wsRef.current = null;
    pcmRef.current?.stop();
    pcmRef.current = null;
    setRecording(false);
    setLiveTranscript('');

    // If handleStopRecording already froze the capture (streaming path was
    // awaited), use that instead of stopping again now — stopping now would
    // include all the dead air recorded while waiting on the failed request.
    const blob = await (safetyBlobPromiseRef.current ?? stopSafetyRecorder());
    safetyBlobPromiseRef.current = null;
    submittingRef.current = false; // release the guard so handleSubmitAudio can run
    if (!blob || blob.size === 0) {
      setAiResponding(false);
      toast.error('No audio captured — please try again.');
      return;
    }
    await handleSubmitAudio(blob);
  }, [stopSafetyRecorder, setAiResponding, handleSubmitAudio]);

  // Start recording: begin the safety-net capture, then try to open the
  // streaming STT WebSocket. If the WS can't connect, keep recording via
  // the safety net alone and transcribe on stop through the REST fallback.
  const handleStartRecording = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    finalizedRef.current = false;
    streamingActiveRef.current = false;

    try {
      await startSafetyRecorder();
    } catch (err) {
      console.error('Microphone access failed:', err);
      toast.error('Could not access your microphone. Please check permissions and try again.');
      submittingRef.current = false;
      return;
    }

    window.speechSynthesis?.cancel();
    setSphereState('idle');

    let ws;
    try {
      ws = new WebSocket(buildStreamUrl(sessionId));
      wsRef.current = ws;
      await new Promise((resolve, reject) => {
        ws.onopen = () => resolve();
        ws.onerror = () => reject(new Error('WebSocket connection failed'));
      });
    } catch (err) {
      console.error('Streaming STT unavailable, falling back to blob upload:', err);
      wsRef.current = null;
      toast.warning('Live captions unavailable — your answer will be transcribed after you stop recording.');
      setRecording(true);
      return;
    }

    streamingActiveRef.current = true;

    ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      if (data.type === 'transcript') {
        setLiveTranscript(data.text || '');
        return;
      }
      if (data.type === 'ai_response') {
        finalizedRef.current = true;
        if (stopTimeoutRef.current) {
          clearTimeout(stopTimeoutRef.current);
          stopTimeoutRef.current = null;
        }
        discardSafetyRecorder();
        safetyBlobPromiseRef.current = null;
        commitStreamedTurn({
          transcribedText: data.transcribed_text,
          turnNumber: data.turn_number,
          interviewerText: data.interviewer_text,
          isFinal: data.is_final,
          currentQuestionIndex: data.current_question_index,
        });
        setLiveTranscript('');
        setSphereState('reviewing');
        scheduleSpeak(data.interviewer_text);
        submittingRef.current = false;
      } else if (data.type === 'error') {
        console.error('Streaming STT error:', data.detail);
        fallbackToBlobSubmit('Live transcription failed — sending your recording instead.');
      }
      ws.close();
    };

    ws.onerror = () => {
      fallbackToBlobSubmit('Connection lost — sending your recording instead.');
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (streamingActiveRef.current && !finalizedRef.current) {
        fallbackToBlobSubmit('Connection lost — sending your recording instead.');
      }
    };

    pcmRef.current = await startPcmCapture((chunk) => {
      if (ws.readyState === WebSocket.OPEN) ws.send(chunk);
    });
    setRecording(true);
  }, [sessionId, commitStreamedTurn, setSphereState, scheduleSpeak, startSafetyRecorder, discardSafetyRecorder, fallbackToBlobSubmit]);

  // Stop recording: if streaming is live, signal the server to flush +
  // finalize the transcript and wait for its ai_response (with a timeout
  // safety net); otherwise submit the safety-net blob directly.
  const handleStopRecording = useCallback(() => {
    pcmRef.current?.stop();
    pcmRef.current = null;
    setRecording(false);

    if (streamingActiveRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
      setAiResponding(true);
      // Freeze the safety-net capture right now, at the moment the student
      // actually finished speaking — not later when/if a fallback triggers,
      // which would otherwise also capture the whole wait-for-response
      // window (now several seconds longer with Gemini's retry backoff) as
      // extra dead air, inflating the fallback upload for no reason.
      safetyBlobPromiseRef.current = stopSafetyRecorder();
      wsRef.current.send(JSON.stringify({ event: 'stop' }));
      stopTimeoutRef.current = setTimeout(() => {
        fallbackToBlobSubmit('Response took too long — sending your recording instead.');
      }, 15000);
      return;
    }

    fallbackToBlobSubmit();
  }, [setAiResponding, fallbackToBlobSubmit, stopSafetyRecorder]);

  // Cleanup any in-flight recording/WS/safety-recorder on unmount
  useEffect(() => {
    return () => {
      pcmRef.current?.stop();
      wsRef.current?.close();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.onstop = null;
        mediaRecorderRef.current.stop();
      }
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
      if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
      // A response that was in flight when this component unmounts must not
      // speak later — window.speechSynthesis is a global API that would
      // happily start talking on whatever page the student is on. Note:
      // this must NOT also set endedRef here — React StrictMode's dev-only
      // mount->cleanup->mount cycle would run this cleanup once on a totally
      // normal mount and never reset the flag, permanently silencing TTS for
      // the rest of the session. Clearing the pending timeout is sufficient;
      // a real end is handled explicitly by handleEnd below.
      if (speakTimeoutRef.current) {
        clearTimeout(speakTimeoutRef.current);
        speakTimeoutRef.current = null;
      }
    };
  }, []);

  // End interview (early-end via /end → marks ENDED_EARLY, no report)
  const handleEnd = useCallback(async () => {
    endedRef.current = true;
    if (speakTimeoutRef.current) {
      clearTimeout(speakTimeoutRef.current);
      speakTimeoutRef.current = null;
    }
    window.speechSynthesis?.cancel();
    await endInterview(sessionId);
    setShowEndConfirm(false);
    navigate('/');
  }, [sessionId, endInterview, navigate]);

  // Auto-navigate to report when the AI signals interview is over
  useEffect(() => {
    if (!isInterviewOver) return;
    // Give a short pause for the AI's final wrap-up TTS to begin
    const t = setTimeout(() => {
      window.speechSynthesis?.cancel();
      navigate(`/report/${sessionId}`);
    }, 2500);
    return () => clearTimeout(t);
  }, [isInterviewOver, sessionId, navigate]);

  // Cleanup TTS on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis?.cancel();
    };
  }, []);

  // Derive session info
  const mode = session?.mode;
  const label = mode === 'company' ? session?.company : session?.topic;
  const questionCount = session?.question_count || 0;
  const currentQ = session?.current_question_index || 0;

  // Loading / error states
  if (loading) {
    return (
      <div className="dark min-h-screen flex items-center justify-center" style={{ background: '#0B0A14' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-xl animate-pulse" style={{ background: 'linear-gradient(180deg, #FFC233 0%, #FF6B1A 100%)' }} />
          <span className="text-sm text-[#9BA1B0]">Loading interview...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dark min-h-screen flex items-center justify-center" style={{ background: '#0B0A14' }}>
        <div className="text-center space-y-3">
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={() => navigate('/')} className="text-sm text-[#FFC233] hover:underline">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!gatePassed) {
    return (
      <PreInterviewGate
        studentName={session?.student_name}
        onStart={() => setGatePassed(true)}
      />
    );
  }

  return (
    <div
      className="dark h-screen flex flex-col overflow-hidden"
      style={{ background: '#0B0A14' }}
      data-testid="interview-room"
    >
      {/* ── Top Bar ── */}
      <header className="h-14 px-4 md:px-6 flex items-center justify-between border-b border-[#1C1F26] shrink-0 z-20">
        <div className="flex items-center gap-3">
          <img src="/botview-logo.png" alt="BotView" className="w-8 h-8 object-contain" />
          <div className="hidden sm:block">
            <span className="text-sm font-semibold text-white font-heading">{label}</span>
            {questionCount > 0 && (
              <span className="text-xs text-[#9BA1B0] ml-2">
                Q {Math.min(currentQ + 1, questionCount)} of {questionCount}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTranscript(!showTranscript)}
            data-testid="toggle-transcript-btn"
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-[#9BA1B0] hover:text-white hover:bg-[#1C1F26] transition-colors"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Transcript
          </button>
          <button
            onClick={() => setShowEndConfirm(true)}
            data-testid="end-interview-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">End</span>
          </button>
        </div>
      </header>

      {/* ── Main content (fills remaining viewport; never grows past it) ── */}
      <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
        {/* Video + Sphere panels — ~60% on desktop, full width on mobile.
            Extra padding + a grey "gutter" background + bordered tiles so
            the video reads as a boxed card (Meet/Zoom-style) instead of
            filling edge-to-edge. Student video on top (larger, for a
            proper shoulders-up frame), AI sphere below. */}
        <div
          className="flex-1 min-h-0 min-w-0 flex flex-col gap-4 p-4 md:p-6 overflow-hidden"
          style={{ background: '#0F1015' }}
        >
          <div className="flex-[3] min-h-0 min-w-0 rounded-2xl border border-[#272B36] overflow-hidden">
            <WebcamPanel
              studentName={session?.student_name}
              cameraOn={cameraOn}
              onToggleCamera={() => setCameraOn(!cameraOn)}
            />
          </div>
          <div className="flex-[2] min-h-0 min-w-0 rounded-2xl border border-[#272B36] overflow-hidden">
            <SphereCanvas sphereState={sphereState} />
          </div>
        </div>

        {/* Transcript sidebar (desktop) — ~40% width, full row height, internal scroll */}
        {showTranscript && (
          <div
            className="hidden md:flex h-full min-h-0 basis-[40%] min-w-[320px] max-w-[560px] border-l border-[#1C1F26] bg-[#0F1015] shrink-0"
            data-testid="transcript-sidebar"
          >
            <TranscriptPanel
              transcript={transcript}
              aiResponding={aiResponding}
              recording={recording}
              liveTranscript={liveTranscript}
            />
          </div>
        )}
      </div>

      {/* ── Mobile transcript drawer ── */}
      <MobileTranscriptDrawer
        transcript={transcript}
        aiResponding={aiResponding}
        recording={recording}
        liveTranscript={liveTranscript}
      />

      {/* ── Footer: full-width, single-line mic/text controls (or the
          post-interview CTA once the session is over) ── */}
      <div className="pt-2 pb-3 md:pt-3 md:pb-4 px-4 md:px-6 border-t border-[#1C1F26] shrink-0">
        {isInterviewOver ? (
          <div className="text-center space-y-3">
            <p className="text-sm text-[#9BA1B0]">Interview complete! Preparing your report...</p>
            <button
              onClick={() => navigate(`/report/${sessionId}`)}
              data-testid="view-report-after-interview"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold font-heading btn-cta"
            >
              View Report
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <InterviewControls
            onSubmitText={handleSubmitText}
            onStartRecording={handleStartRecording}
            onStopRecording={handleStopRecording}
            recording={recording}
            disabled={aiResponding}
            micOn={true}
          />
        )}
      </div>

      {/* ── End Confirmation Modal ── */}
      {showEndConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#14161B] border border-[#272B36] rounded-2xl p-6 max-w-sm w-full space-y-4" data-testid="end-confirm-modal">
            <h3 className="text-base font-heading font-semibold text-white">End Interview?</h3>
            <p className="text-sm text-[#9BA1B0]">
              The session will be marked as ended early — no report will be generated. Your progress so far is saved.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowEndConfirm(false)}
                className="flex-1 px-4 py-2.5 rounded-lg text-sm bg-[#1C1F26] text-white hover:bg-[#272B36] transition-colors"
              >
                Continue
              </button>
              <button
                onClick={handleEnd}
                data-testid="confirm-end-btn"
                className="flex-1 px-4 py-2.5 rounded-lg text-sm bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
              >
                End Interview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Mobile: collapsible transcript at the bottom */
function MobileTranscriptDrawer({ transcript, aiResponding, recording, liveTranscript }) {
  const [open, setOpen] = useState(false);

  // Auto-expand while recording so live transcript is visible without a tap.
  useEffect(() => {
    if (recording) setOpen(true);
  }, [recording]);

  return (
    <div className="md:hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-[#9BA1B0] border-t border-[#1C1F26]"
        data-testid="mobile-transcript-toggle"
      >
        <MessageSquare className="w-3.5 h-3.5" />
        {open ? 'Hide' : 'Show'} Transcript ({transcript.length})
      </button>
      {open && (
        <div className="h-48 border-t border-[#1C1F26] bg-[#0F1015]">
          <TranscriptPanel
            transcript={transcript}
            aiResponding={aiResponding}
            recording={recording}
            liveTranscript={liveTranscript}
          />
        </div>
      )}
    </div>
  );
}
